import { Box, Grid, Fade, Slide, Fab, Tooltip, useTheme } from "@mui/material"
import { FormLogin } from "../../components";
import { useState, useEffect, useContext } from "react";
import { ColorModeContext } from "../../themes/themes";
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

const LoginScreen = () => {
    const [showContent, setShowContent] = useState(false);
    const [showLogo, setShowLogo] = useState(false);
    const theme = useTheme();
    const colorMode = useContext(ColorModeContext);
    const isDarkMode = theme.palette.mode === 'dark';

    useEffect(() => {
        // Activar animaciones secuencialmente
        setShowContent(true);
        const logoTimer = setTimeout(() => {
            setShowLogo(true);
        }, 300);

        return () => clearTimeout(logoTimer);
    }, []);

    return (
        <Grid container xs={12} sx={{ width: '100%', height: '100vh' }}>
            {/* FAB para cambiar tema */}
            <Tooltip title={isDarkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"} placement="left">
                <Fab
                    color="primary"
                    aria-label="toggle theme"
                    onClick={colorMode.toggleColorMode}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 1000,
                        background: isDarkMode 
                            ? 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: isDarkMode ? '#333' : '#fff',
                        boxShadow: isDarkMode 
                            ? '0 4px 20px rgba(252, 182, 159, 0.5)'
                            : '0 4px 20px rgba(102, 126, 234, 0.5)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                            transform: 'scale(1.1) rotate(15deg)',
                            background: isDarkMode 
                                ? 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: isDarkMode 
                                ? '0 6px 25px rgba(252, 182, 159, 0.7)'
                                : '0 6px 25px rgba(102, 126, 234, 0.7)',
                        }
                    }}
                >
                    {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </Fab>
            </Tooltip>

            <Slide direction="right" in={showContent} timeout={800}>
                <Grid item xs={12} md={6}>
                    <Box 
                        sx={{
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            width: '100%', 
                            height: '100vh',
                            background: isDarkMode 
                                ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                                : 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'background 0.3s ease-in-out',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: isDarkMode
                                    ? 'radial-gradient(circle at 20% 20%, rgba(102, 126, 234, 0.15) 0%, transparent 50%)'
                                    : 'radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.05) 0%, transparent 50%)',
                                animation: 'float 6s ease-in-out infinite',
                            },
                            '@keyframes float': {
                                '0%, 100%': { 
                                    transform: 'translate(0, 0) scale(1)',
                                    opacity: 0.5 
                                },
                                '50%': { 
                                    transform: 'translate(-10px, -10px) scale(1.1)',
                                    opacity: 0.8 
                                }
                            }
                        }}
                    >
                        <FormLogin />
                    </Box>
                </Grid>
            </Slide>
            <Fade in={showContent} timeout={1000}>
                <Grid item xs={0} md={6}>
                    <Box
                        sx={{
                            backgroundImage: 'url(/img/logoFondo.png)',
                            width: '100%',
                            height: '100vh',
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat',
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'hidden',
                            backgroundPosition: 'center',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: isDarkMode
                                    ? 'linear-gradient(135deg, rgba(26, 26, 46, 0.0) 0%, rgba(22, 33, 62, 0.5) 100%)'
                                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(3, 100, 255, 0.01) 100%)',
                                transition: 'background 0.3s ease-in-out',
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: isDarkMode
                                    ? 'radial-gradient(circle at 50% 50%, rgba(100, 181, 246, 0.1) 0%, transparent 60%)'
                                    : 'radial-gradient(circle at 50% 50%, rgba(3, 100, 255, 0.08) 0%, transparent 60%)',
                                animation: 'pulse 4s ease-in-out infinite',
                            },
                            '@keyframes pulse': {
                                '0%, 100%': { 
                                    opacity: 0.5,
                                    transform: 'scale(1)'
                                },
                                '50%': { 
                                    opacity: 0.8,
                                    transform: 'scale(1.05)'
                                }
                            }
                        }}
                    >
                        <Slide direction="up" in={showLogo} timeout={1200}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <img
                                    src={isDarkMode ? "/img/Zeta-white.png" : "/img/Zeta-black.png"}
                                    alt="Zeta Logo"
                                    style={{
                                        maxWidth: '70%',
                                        height: 'auto',
                                        filter: isDarkMode 
                                            ? 'drop-shadow(0 4px 30px rgba(100, 181, 246, 0.8))'
                                            : 'drop-shadow(0 4px 30px rgba(3, 100, 255, 0.2))',
                                        animation: 'logoFloat 3s ease-in-out infinite',
                                        transition: 'filter 0.3s ease-in-out',
                                    }}
                                />
                            </Box>
                        </Slide>
                    </Box>
                </Grid>
            </Fade>

            {/* Keyframes para el logo */}
            <style jsx>{`
                @keyframes logoFloat {
                    0%, 100% { 
                        transform: scale(0.8) translateY(0px);
                    }
                    50% { 
                        transform: scale(0.82) translateY(-5px);
                    }
                }
            `}</style>
        </Grid>
    )
}

export default LoginScreen